/**
 * Users Service
 * Handles user-related API operations
 */

import { apiClient } from '@/lib/api-client';
import { API_ENDPOINTS } from '@/config/api.config';
import { PaginatedResponse } from './leadsService';

// Types
export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  phone?: string;
  avatar?: string;
  
  // Company (Tenant)
  company: string; // UUID
  company_name?: string;
  
  // Role and Permissions
  // Platform roles (OWLY team): owly_admin, owly_support, owly_3d
  // Tenant roles: admin, manager, project_manager, sales_leader, sales_advisor, sales, marketing, support, reviewer
  role: 'owly_admin' | 'owly_support' | 'owly_3d' | 'admin' | 'manager' | 'project_manager' | 'sales_leader' | 'sales_advisor' | 'sales' | 'marketing' | 'support' | 'reviewer';
  status: 'active' | 'inactive' | 'suspended';
  
  // Assignments
  assigned_projects?: string[]; // UUIDs
  territories?: any[];
  
  // Performance
  performance_metrics?: Record<string, any>;
  sales_target?: Record<string, any>;
  
  // Settings
  settings?: Record<string, any>;
  signature?: string;
  calendar_url?: string;
  social_media?: Record<string, any>;
  
  // Manager
  manager?: string; // UUID
  
  // Django fields
  is_staff: boolean;
  is_active: boolean;
  is_superuser?: boolean;
  is_manager?: boolean;
  date_joined: string;
  last_login?: string;
  created_at: string;
  updated_at: string;
}

export interface UserCreateData {
  email: string;
  password?: string;
  password_confirm?: string;
  first_name: string;
  last_name: string;
  phone?: string;
  company?: string; // UUID - Optional (admins can omit this or specify)
  // Platform roles (OWLY team): owly_admin, owly_support, owly_3d
  // Tenant roles: admin, manager, project_manager, sales_leader, sales_advisor, sales, marketing, support, reviewer
  role?: 'owly_admin' | 'owly_support' | 'owly_3d' | 'admin' | 'manager' | 'project_manager' | 'sales_leader' | 'sales_advisor' | 'sales' | 'marketing' | 'support' | 'reviewer';
  status?: 'active' | 'inactive' | 'suspended';
  assigned_projects?: string[];  // Legacy - use assigned_projects_ids instead
  assigned_projects_ids?: string[];  // Preferred - creates ProjectMembership records
  territories?: any[];
  manager?: string;
}

export interface UserUpdateData {
  first_name?: string;
  last_name?: string;
  phone?: string;
  avatar?: string;
  role?: 'owly_admin' | 'owly_support' | 'owly_3d' | 'admin' | 'manager' | 'project_manager' | 'sales_leader' | 'sales_advisor' | 'marketing' | 'support' | 'sales' | 'reviewer';
  status?: 'active' | 'inactive' | 'suspended';
  assigned_projects?: string[];
  assigned_projects_ids?: string[];  // Alternative field for updating projects (creates ProjectMembership)
  territories?: any[];
  sales_target?: Record<string, any>;
  settings?: Record<string, any>;
  signature?: string;
  calendar_url?: string;
  social_media?: Record<string, any>;
  manager?: string;
}

/**
 * Users Service
 */
export const usersService = {
  /**
   * Get all users (filtered by company if not superuser)
   */
  async getUsers(params?: Record<string, any>): Promise<PaginatedResponse<User>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const endpoint = params 
      ? `${API_ENDPOINTS.USERS.BASE}?${queryParams.toString()}`
      : API_ENDPOINTS.USERS.BASE;
    
    return apiClient.get<PaginatedResponse<User>>(endpoint);
  },

  /**
   * Get user by ID
   */
  async getUserById(id: string): Promise<User> {
    return apiClient.get<User>(API_ENDPOINTS.USERS.DETAIL(id));
  },

  /**
   * Create new user
   */
  async createUser(data: UserCreateData): Promise<User> {
    return apiClient.post<User>(API_ENDPOINTS.USERS.BASE, data);
  },

  /**
   * Update user
   * @param companyId - Pass the target user's company ID so platform superusers
   *                    can update users from other companies without 404.
   */
  async updateUser(id: string, data: UserUpdateData, companyId?: string): Promise<User> {
    const url = companyId
      ? `${API_ENDPOINTS.USERS.DETAIL(id)}?company=${companyId}`
      : API_ENDPOINTS.USERS.DETAIL(id);
    return apiClient.patch<User>(url, data);
  },

  /**
   * Delete user
   * @param companyId - Pass the target user's company ID for cross-company access.
   */
  async deleteUser(id: string, companyId?: string): Promise<void> {
    const url = companyId
      ? `${API_ENDPOINTS.USERS.DETAIL(id)}?company=${companyId}`
      : API_ENDPOINTS.USERS.DETAIL(id);
    return apiClient.delete<void>(url);
  },

  /**
   * Deactivate user
   */
  async deactivateUser(id: string): Promise<User> {
    return apiClient.post<User>(API_ENDPOINTS.USERS.DEACTIVATE(id), {});
  },

  /**
   * Activate user
   */
  async activateUser(id: string): Promise<User> {
    return apiClient.post<User>(API_ENDPOINTS.USERS.ACTIVATE(id), {});
  },

  /**
   * Reenviar invitación CRM con nueva contraseña temporal
   * @param companyId - Pass the target user's company ID for cross-company access.
   */
  async resendInvitation(id: string, companyId?: string): Promise<{ message: string; invitation_sent: boolean }> {
    const url = companyId
      ? `${API_ENDPOINTS.USERS.DETAIL(id)}resend-invitation/?company=${companyId}`
      : `${API_ENDPOINTS.USERS.DETAIL(id)}resend-invitation/`;
    return apiClient.post<{ message: string; invitation_sent: boolean }>(url, {});
  },

  /**
   * Get user performance metrics
   */
  async getUserPerformance(id: string): Promise<any> {
    return apiClient.get<any>(API_ENDPOINTS.USERS.PERFORMANCE(id));
  },

  /**
   * Assign custom role to user
   */
  async assignRole(userId: string, roleId: string | null): Promise<{ message: string; user: User }> {
    return apiClient.post<{ message: string; user: User }>(
      `/auth/users/${userId}/assign-role/`,
      { role_id: roleId }
    );
  },
};
